import React, { useState, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import Nav from "../Nav"

const PER_PAGE = 50

export default function BuildersRiskSubmissionsReport({ user }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || "")
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || "")
  const [email, setEmail] = useState(searchParams.get("email") || "")
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10))
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 0 })
  const [selectedSubmission, setSelectedSubmission] = useState(null)

  const updateSearchParams = (start, end, emailVal, pageNum) => {
    const params = new URLSearchParams()
    if (start) params.set("start_date", start)
    if (end) params.set("end_date", end)
    if (emailVal) params.set("email", emailVal)
    params.set("page", pageNum)
    setSearchParams(params)
  }

  const fetchData = (start, end, emailVal, pageNum = 1) => {
    setLoading(true)
    setError(null)

    const params = new URLSearchParams()
    if (start) params.append("start_date", start)
    if (end) params.append("end_date", end)
    if (emailVal) params.append("email", emailVal)
    params.append("page", pageNum)
    params.append("per_page", PER_PAGE)

    const url = `/api/reports/builders_risk_submissions?${params}`

    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((json) => {
        setData(json.data || [])
        setPagination(json.pagination || {})
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData(startDate, endDate, email, page)
  }, [])

  const handleApplyFilter = (e) => {
    e.preventDefault()
    setPage(1)
    updateSearchParams(startDate, endDate, email, 1)
    fetchData(startDate, endDate, email, 1)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    setEmail("")
    setPage(1)
    updateSearchParams("", "", "", 1)
    fetchData("", "", "", 1)
  }

  const handlePageChange = (newPage) => {
    setPage(newPage)
    updateSearchParams(startDate, endDate, email, newPage)
    fetchData(startDate, endDate, email, newPage)
  }

  const handleDownloadCSV = () => {
    const params = new URLSearchParams()
    if (startDate) params.append("start_date", startDate)
    if (endDate) params.append("end_date", endDate)
    if (email) params.append("email", email)
    params.append("format", "csv")

    const url = `/api/reports/builders_risk_submissions?${params}`
    window.location.href = url
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  if (selectedSubmission) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Nav user={user} />

        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedSubmission(null)}
            className="mb-6 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to List
          </button>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Builders Risk Submission Details</h2>
              <div className="mt-2 flex gap-6">
                <p className="text-gray-600">ID: {selectedSubmission.id}</p>
                <p className="text-gray-600">
                  Hawksoft Client ID: <span className="font-semibold text-gray-900">{selectedSubmission.hawksoft_client_id || "—"}</span>
                </p>
              </div>
            </div>

            <div className="px-5 py-6 sm:px-6">
              <div className="space-y-8">
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['email', 'phone', 'first_name', 'last_name'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Builder & Mailing Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Builder Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['builder_name', 'builder_city_state', 'form_of_business', 'company_name'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Project/Building Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Building Address</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['building_street', 'building_street2', 'building_city', 'building_state', 'building_zip'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Building & Project Details */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Building & Project Details</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['building_type', 'number_of_stories', 'square_footage', 'square_footage_after_renovation', 'construction_type', 'project_type'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Project Timeline */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Timeline</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['project_start_date', 'expected_complete_date', 'coverage_date', 'percent_complete'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined ? "N/A" : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Coverage & Coverage Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Coverage Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['building_coverage', 'existing_structure_value', 'existing_structure_value_new_const', 'estimated_building_coverage', 'renovation_value', 'annual_premium', 'deductible'].map(key => {
                      const value = selectedSubmission[key]
                      if (value === null || value === undefined) return null
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Risk Assessment */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['is_high_risk', 'high_risk_fire_zone', 'near_coast', 'has_basement', 'claims_in_past_2_years', 'structure_condition'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {value === null || value === undefined
                              ? "N/A"
                              : typeof value === "boolean"
                                ? value ? "Yes" : "No"
                                : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Other Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Other Information</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['general_contractor_info', 'provide_social_security', 'user_type', 'source', 'referred_by', 'locale'].map(key => {
                      const value = selectedSubmission[key]
                      if (value === null || value === undefined) return null
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-900 break-words">
                            {typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Timestamps */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {['created_at', 'updated_at'].map(key => {
                      const value = selectedSubmission[key]
                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-500 mb-1">
                            {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </dt>
                          <dd className="text-sm text-gray-600">
                            {value ? new Date(value).toLocaleString() : "N/A"}
                          </dd>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Nav user={user} />

      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-2">
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">Builders Risk Submissions</span>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-6 sm:px-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Builders Risk Submissions</h2>
                <p className="mt-2 text-gray-600">
                  Total: {pagination.total_count || 0} submission{pagination.total_count !== 1 ? "s" : ""}
                </p>
              </div>
              {selectedSubmission && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Hawksoft Client ID</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {selectedSubmission.hawksoft_client_id || "—"}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-6 sm:px-6 border-b border-gray-200 bg-gray-50">
            <form onSubmit={handleApplyFilter} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    placeholder="Search by email..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilter}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleDownloadCSV}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium text-sm"
                >
                  📥 Download CSV
                </button>
              </div>
            </form>
          </div>

          <div className="overflow-x-auto">
            {loading && (
              <div className="px-6 py-8 text-center text-gray-600">
                Loading...
              </div>
            )}

            {error && (
              <div className="px-6 py-8 text-center text-red-600">
                Error: {error}
              </div>
            )}

            {!loading && !error && data?.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-600">
                No data available
              </div>
            )}

            {!loading && !error && data?.length > 0 && (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-40">Email</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-32">Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-32">Builder</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/5 min-w-28">Building Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">Premium</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 w-1/6 min-w-28">Submitted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedSubmission(row)}
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.email}>{row.email}</td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={`${row.first_name} ${row.last_name}`}>
                          {row.first_name} {row.last_name}
                        </td>
                        <td className="px-4 py-3 text-gray-900 truncate" title={row.builder_name || "N/A"}>{row.builder_name || "N/A"}</td>
                        <td className="px-4 py-3 text-gray-700 truncate" title={row.building_type || "N/A"}>{row.building_type || "—"}</td>
                        <td className="px-4 py-3 text-gray-700 truncate" title={row.annual_premium || "N/A"}>{row.annual_premium ? `$${row.annual_premium}` : "—"}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="text-sm text-gray-600">
                    Page {page} of {pagination.total_pages || 1}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.total_pages, page + 1))}
                      disabled={page === pagination.total_pages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
